/**
 * Modal SeletorUsinasEolicas (WINDX-021 Fase 3 v2).
 *
 * Modal de selecao de usinas eolicas (subdivisao=4) com:
 *   - Single-fetch de ~1052 usinas em uma resposta (~80KB gzip)
 *   - Filtros client-side: busca textual + UF + subsistema + conjunto
 *   - Tabela virtualizada (@tanstack/react-virtual): ~20 linhas no DOM
 *   - Ordenacao client-side por coluna clicavel
 *   - Selecao multipla com Set<number> em useRef (toggle <16ms)
 *
 * Substitui o caminho `subdivisao === 4` do antigo SeletorEntidades
 * (que rolava 1052 checkboxes num grid simples). O SeletorEntidades
 * legado continua para subdivisao 1/2/3 (subsistemas/regioes/conjuntos).
 *
 * Contrato com o pai (CabecalhoGrafico):
 *   - `show`, `onHide`: controle de visibilidade
 *   - `permiteSelecaoVazia`: comportamento de "nenhuma marcada"
 *     (so vale pra Comparativo SM)
 *   - Acessa Redux direto (`selectWindxIdsEntidades`, `setIdsEntidades`,
 *     `realizarConsulta`) — mesmo contrato do SeletorEntidades.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';

import { useGetUsinasEolicasQuery } from 'services/api/windx';
import {
  OrdenacaoUsinas,
  UsinaEolicaDenormalizada,
} from 'helper/windx';
import filtrarUsinasEolicas from 'helper/filtrarUsinasEolicas';
import ordenarUsinasEolicas from 'helper/ordenarUsinasEolicas';
import useSelecaoUsinas from 'hooks/Windx/useSelecaoUsinas';
import useFiltrosUsinas from 'hooks/Windx/useFiltrosUsinas';
import {
  selectWindxIdsEntidades,
  selectWindxTelaAtiva,
  setIdsEntidades,
} from 'redux/reducers/windxControlPanelReducer';

import BarraDeFiltros from './BarraDeFiltros';
import ResumoSelecao from './ResumoSelecao';
import TabelaUsinas from './TabelaUsinas';

import './index.scss';

interface SeletorUsinasEolicasProps {
  show: boolean;
  onHide: () => void;
  permiteSelecaoVazia?: boolean;
}

function extrairUnicos<T>(
  itens: UsinaEolicaDenormalizada[],
  campo: (u: UsinaEolicaDenormalizada) => T | null,
): T[] {
  const set = new Set<T>();
  itens.forEach((u) => {
    const v = campo(u);
    if (v !== null && v !== undefined && v !== '') set.add(v);
  });
  return Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));
}

export default function SeletorUsinasEolicas({
  show,
  onHide,
  permiteSelecaoVazia = false,
}: SeletorUsinasEolicasProps) {
  const dispatch = useDispatch();
  const idsSelecionadosRedux = useSelector(selectWindxIdsEntidades);
  const telaAtiva = useSelector(selectWindxTelaAtiva);

  const usinasQuery = useGetUsinasEolicasQuery(undefined, { skip: !show });
  const usinas = useMemo<UsinaEolicaDenormalizada[]>(
    () => usinasQuery.data?.data ?? [],
    [usinasQuery.data],
  );

  const filtrosHook = useFiltrosUsinas();
  const selecao = useSelecaoUsinas({ inicial: idsSelecionadosRedux });
  const [ordenacao, setOrdenacao] = useState<OrdenacaoUsinas>({
    coluna: 'codigo',
    direcao: 'asc',
  });

  // Sincroniza buffer local com Redux quando o modal abre.
  useEffect(() => {
    if (show) {
      selecao.resetar(idsSelecionadosRedux);
      filtrosHook.limparFiltros();
    }
    // Intencional: omitir `selecao`/`filtrosHook` para nao resetar
    // a cada toggle interno.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Listas dos selects extraidas do dataset (sem chamadas extras).
  const ufsDisponiveis = useMemo(() => extrairUnicos(usinas, (u) => u.uf_sigla), [usinas]);
  const subsistemasDisponiveis = useMemo(
    () => extrairUnicos(usinas, (u) => u.subsistema_sm_sigla),
    [usinas],
  );
  // WINDX-024: dropdown filtro "Conjunto" exibe label_conjunto (desambiguado)
  // em vez de nome_conjunto. value enviado ao filtro tambem eh o label.
  const conjuntosDisponiveis = useMemo(
    () => extrairUnicos(usinas, (u) => u.label_conjunto),
    [usinas],
  );

  const usinasFiltradas = useMemo(
    () => filtrarUsinasEolicas(usinas, filtrosHook.filtrosEfetivos),
    [usinas, filtrosHook.filtrosEfetivos],
  );
  const usinasOrdenadas = useMemo(
    () => ordenarUsinasEolicas(usinasFiltradas, ordenacao),
    [usinasFiltradas, ordenacao],
  );

  const usinasPorId = useMemo(() => {
    const m = new Map<number, UsinaEolicaDenormalizada>();
    usinas.forEach((u) => m.set(u.id, u));
    return m;
  }, [usinas]);

  const handleAplicar = () => {
    // 2026-05-29: apos aplicar a selecao, NAO dispara realizarConsulta
    // automaticamente. setIdsEntidades ja marca consultaPendente=true; o
    // usuario decide quando recarregar pelo botao "Realizar Consulta" do
    // painel — janela necessaria para que ele leia o aviso de complexidade
    // (MedidorComplexidade) antes de enviar a query. Mesmo padrao adotado
    // no ModalCadastrar de simulacoes antigas (decisao PO 2026-05-26).
    dispatch(setIdsEntidades(selecao.ids));
    onHide();
  };

  const totalSelecionadas = selecao.count;
  const totalMw = selecao.totalPotencia(usinasPorId);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      className="windx-seletor-usinas-modal"
      data-testid="windx-seletor-usinas-modal"
      aria-labelledby="windx-seletor-usinas-titulo"
    >
      <Modal.Header closeButton>
        <Modal.Title id="windx-seletor-usinas-titulo" className="h6 mb-0">
          Selecionar usinas eólicas
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <>
          {usinasQuery.isLoading && (
            <div className="p-3 text-center">
              <Spinner animation="border" role="status" size="sm" />
              <span className="ms-2">Carregando usinas...</span>
            </div>
          )}
          {usinasQuery.error && (
            <Alert variant="danger" className="m-0">
              Não foi possível carregar as usinas. Tente novamente em instantes.
            </Alert>
          )}
          {usinasQuery.data && (
          <div className="windx-seletor-usinas">
            <BarraDeFiltros
              busca={filtrosHook.filtros.busca}
              onBuscaChange={filtrosHook.setBusca}
              ufsDisponiveis={ufsDisponiveis}
              ufsSelecionadas={filtrosHook.filtros.ufs}
              onUfsChange={filtrosHook.setUfs}
              subsistemasDisponiveis={subsistemasDisponiveis}
              subsistemasSelecionados={filtrosHook.filtros.subsistemasSm}
              onSubsistemasChange={filtrosHook.setSubsistemasSm}
              conjuntosDisponiveis={conjuntosDisponiveis}
              conjuntosSelecionados={filtrosHook.filtros.conjuntos}
              onConjuntosChange={filtrosHook.setConjuntos}
            />
            <TabelaUsinas
              usinas={usinasOrdenadas}
              isSelecionada={selecao.isSelecionada}
              onToggle={selecao.toggle}
              onSelecionarVisiveis={selecao.selecionarTodas}
              onDesselecionarVisiveis={selecao.desselecionarTodas}
              ordenacao={ordenacao}
              onOrdenacaoChange={setOrdenacao}
            />
            <ResumoSelecao
              selecionadas={totalSelecionadas}
              total={usinas.length}
              totalFiltrados={usinasOrdenadas.length}
              totalPotenciaMw={totalMw}
            />
            {permiteSelecaoVazia && telaAtiva === 'sm' && totalSelecionadas === 0 && (
              <div className="windx-seletor-usinas__hint">
                <strong>Nenhuma marcada inclui todas</strong>
                {' — a consulta vai retornar dados das '}
                {usinas.length.toLocaleString('pt-BR')}
                {' usinas.'}
              </div>
            )}
          </div>
          )}
        </>
      </Modal.Body>
      <Modal.Footer>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={selecao.limpar}
          disabled={totalSelecionadas === 0}
        >
          Limpar
        </Button>
        <Button size="sm" onClick={handleAplicar}>
          Aplicar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
